import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { User } from './access/user.model';
import { NgStyle } from '@angular/common';
import { randomCigpColor } from './utils/utils';
import { UsersService } from './access/users.service';
import { of } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'of-avatar-card',
  imports: [NgStyle],
  template: `
    @if (user()) {
      <div class="d-flex gap-2 align-items-center">
        @if (profilePicture.value(); as picture) {
          <img
            [src]="picture"
            class="e-avatar e-avatar-small e-avatar-circle"
            [alt]="user()?.name"
            [title]="title()"
            [ngStyle]="styles()"
          />
        } @else {
          <div
            class="e-avatar e-avatar-small e-avatar-circle text-white d-flex justify-content-center align-items-center"
            [ngStyle]="{ 'background-color': backgroundColor, ...styles() }"
            [title]="title()"
          >
            {{ user()?.initials }}
          </div>
        }

        @if (showName()) {
          <div>{{ user()?.name }}</div>
        }
      </div>
    }
  `
})
export class AvatarCardComponent {
  public backgroundColor = randomCigpColor();
  private userService = inject(UsersService);

  public user = input<User | null>(null);

  protected readonly profilePicture = rxResource({
    params: () => this.user()?.email ?? null,
    stream: ({ params: email }) => (email ? this.userService.getProfilePicture$(email) : of(null))
  });
  public showName = input(false);
  public titlePrefix = input<string>('');
  /**
   * Extra inline styles for the avatar element. Prefer setting only `--of-avatar-size`
   * — height/width and the initials font-size are both derived from it in `styles.scss`.
   */
  public styles = input<Record<string, string>>({ '--of-avatar-size': '24px' });

  /** e.g. `Approved by Jane Doe`, or just `Jane Doe` when no prefix is supplied. */
  public title = computed(() => {
    const name = this.user()?.name;
    if (!name) {
      return '';
    }

    const prefix = this.titlePrefix();

    return prefix.length > 0 ? `${prefix} ${name}` : name;
  });
}
